import { useEffect, useState, type ReactNode } from "react";
import { Button, Dialog, TextField, vars } from "@pack-it-up/ui-web";

// Issue #8 — the one "name the copy" prompt behind duplicating a template, saving a
// trip as a template and copying a trip. Only the wording and the submit handler
// differ, so the three flows share this rather than each growing a modal.
export interface CopyDialogProps {
  open: boolean;
  title: string;
  subtitle: string;
  label: string;
  /** Prefilled name, normally `copyName(source.name)`. */
  defaultName: string;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  /**
   * When set, the dialog also asks for a start date for the copy. Copying a trip needs
   * one: to-dos travel as an offset from start (Doc 07 §4), so the new dates are what
   * turn "a week before" back into a real date.
   */
  startDateLabel?: string;
  onClose: () => void;
  onConfirm: (name: string, startDate?: string) => void;
}

export const CopyDialog = ({
  open,
  title,
  subtitle,
  label,
  defaultName,
  confirmLabel,
  busy,
  error,
  startDateLabel,
  onClose,
  onConfirm,
}: CopyDialogProps): ReactNode => {
  const [name, setName] = useState(defaultName);
  const [startDate, setStartDate] = useState("");

  // Reopening for a different source must not show the previous one's name.
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setStartDate("");
    }
  }, [open, defaultName]);

  const submit = (): void => {
    if (name.trim() === "" || busy) return;
    onConfirm(name.trim(), startDate === "" ? undefined : startDate);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={name.trim() === "" || busy}>
            {busy ? "Copying…" : confirmLabel}
          </Button>
        </>
      }
    >
      <TextField label={label} value={name} onChange={setName} autoFocus />
      {startDateLabel !== undefined && (
        <div style={{ marginTop: vars.space.md }}>
          <TextField label={startDateLabel} type="date" value={startDate} onChange={setStartDate} />
        </div>
      )}
      {error !== null && (
        <p role="alert" style={{ color: vars.color.danger, fontSize: vars.font.sizeXs, marginTop: vars.space.sm }}>
          {error}
        </p>
      )}
    </Dialog>
  );
};
